import api from './api';
import type { UserPlanInfo, InvoiceDto, PlanType, BillingCycle } from '../types';

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  message?: string;
}

/**
 * Récupère les informations du plan de l'utilisateur
 */
export async function getPlanInfo(): Promise<ApiResponse<UserPlanInfo>> {
  const response = await api.get('/subscription/plan');
  return response.data;
}

/**
 * Crée une session Stripe Checkout
 */
export async function createCheckout(
  plan: 'premium' | 'duo',
  billingCycle: BillingCycle,
  successUrl: string,
  cancelUrl: string,
): Promise<ApiResponse<{ url: string }>> {
  const response = await api.post('/subscription/checkout', {
    plan,
    billingCycle,
    successUrl,
    cancelUrl,
  });
  return response.data;
}

/**
 * Crée une session Stripe Customer Portal
 */
export async function createPortalSession(): Promise<ApiResponse<{ url: string }>> {
  const response = await api.post('/subscription/portal');
  return response.data;
}

/**
 * Récupère les factures de l'utilisateur
 */
export async function getInvoices(): Promise<ApiResponse<InvoiceDto[]>> {
  const response = await api.get('/subscription/invoices');
  return response.data;
}

/**
 * Annule l'abonnement (effectif à la fin de la période)
 */
export async function cancelSubscription(): Promise<ApiResponse<void>> {
  const response = await api.post('/subscription/cancel');
  return response.data;
}

/**
 * Reprend un abonnement annulé
 */
export async function resumeSubscription(): Promise<ApiResponse<void>> {
  const response = await api.post('/subscription/resume');
  return response.data;
}

/**
 * Traite la sélection de downgrade
 */
export async function processDowngradeSelection(selection: {
  keepBankAccountIds?: string[];
  keepBudgetIds?: string[];
  keepEnvelopeIds?: string[];
}): Promise<ApiResponse<void>> {
  const response = await api.post('/subscription/downgrade-selection', selection);
  return response.data;
}

/**
 * Aperçu du prorata pour un changement de plan
 */
export async function previewPlanChange(
  targetPlan: PlanType,
  billingCycle: BillingCycle,
): Promise<ApiResponse<{ amountDue: number; currency: string; credit: number; newPlanCost: number; immediatePayment: boolean }>> {
  const response = await api.post('/subscription/preview-change', { targetPlan, billingCycle });
  return response.data;
}

/**
 * Change de plan (upgrade immédiat / downgrade programmé en fin de période)
 */
export async function changePlan(
  targetPlan: PlanType,
  billingCycle: BillingCycle,
): Promise<ApiResponse<{ type: 'immediate' | 'scheduled'; effectiveDate?: string }>> {
  const response = await api.post('/subscription/change-plan', { targetPlan, billingCycle });
  return response.data;
}

/**
 * Annule un changement de plan programmé
 */
export async function cancelPendingChange(): Promise<ApiResponse<void>> {
  const response = await api.post('/subscription/cancel-pending-change');
  return response.data;
}

/**
 * Admin : définir un plan offert pour un utilisateur
 */
export async function setGrantedPlan(
  userId: string,
  plan: PlanType | null,
): Promise<ApiResponse<void>> {
  const response = await api.put(`/admin/users/${userId}/granted-plan`, { plan });
  return response.data;
}
